import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { usd, pct, num } from "@/lib/format";
import { KpiValue } from "@/components/common/KpiValue";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/shell/PageHeader";
import { Panel, ProvenanceTag, StatusBadge, TableSkeleton } from "@/components/common/primitives";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

const PERIODS = [["30d", "30D"], ["90d", "90D"], ["ytd", "YTD"]];
const segActive = "ez-seg-active";
const segIdle = "ez-seg text-ink-secondary";

export default function SalesProfit() {
  const [d, setD] = useState(null);
  const [period, setPeriod] = useState("30d");
  useEffect(() => { api.sales().then((r) => setD(r.data)); }, []);

  if (!d) return (
    <div className="space-y-4">
      <PageHeader title="Sales & Profit" description="Operational profit intelligence with traceable COGS and settlement reconciliation." meta={<ProvenanceTag kind="fixture" />} />
      <Panel className="p-4"><TableSkeleton rows={8} /></Panel>
    </div>
  );

  const max = Math.max(...d.waterfall.map((x) => Math.abs(x.value)));
  const costRows = d.waterfall.filter((w) => w.value < 0);
  const costMax = Math.max(...costRows.map((c) => Math.abs(c.value)), 1);

  return (
    <div className="space-y-4" data-testid="sales-page">
      <PageHeader title="Sales & Profit"
        description="Operational profit intelligence with traceable COGS and settlement reconciliation. Not a general ledger."
        meta={<><ProvenanceTag kind="fixture" /><StatusBadge tone="warning">Partial — Walmart feeds unavailable</StatusBadge></>}
        actions={
          <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll text-micro font-mono" role="group" aria-label="Period">
            {PERIODS.map(([k, l]) => (
              <button key={k} data-testid={`sales-period-${k}`} aria-pressed={period === k} onClick={() => setPeriod(k)}
                className={cn("px-2.5 h-8", period === k ? segActive : segIdle)}>{l}</button>
            ))}
          </div>
        } />

      <Panel className="px-1 py-1" testid="sales-kpis">
        <div className="flex flex-wrap items-stretch divide-x divide-edge-subtle">
          {d.kpis.map((k) => (
            <div key={k.key} className="px-4 py-2.5 flex-1 min-w-[135px]" data-testid={`kpi-${k.key}`}>
              <div className="text-micro font-mono uppercase tracking-wide text-ink-muted">{k.label}</div>
              <div className="metric-result text-metricsm font-mono mt-0.5 text-ink-primary">{k.unit === "%" ? <span className="metric-value">{pct(k.value)}</span> : k.key === "units" ? <span className="metric-value">{num(Math.round(k.value))}</span> : <KpiValue value={k.value} label={k.label} className="text-metricsm font-mono text-ink-primary" />}</div>
              <div className={cn("inline-flex items-center gap-0.5 text-micro font-mono mt-0.5", k.delta >= 0 ? "text-emerald-600" : "text-red-600")}>
                {k.delta >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{Math.abs(k.delta)}%
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <div className="grid lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-4">
        <Panel className="p-4" testid="profit-waterfall">
          <div className="text-stitle text-ink-primary mb-3">Contribution profit waterfall</div>
          <div className="space-y-1.5">
            {d.waterfall.map((w, i) => (
              <div key={i} className="flex items-center gap-3 text-compact">
                <span className="w-40 shrink-0 text-ink-secondary truncate">{w.label}</span>
                <div className="flex-1 h-5 bg-surface-2 rounded-ctrl overflow-hidden">
                  <div className={cn("h-full rounded-ctrl", w.value < 0 ? "bg-red-500/80" : w.label.includes("Profit") ? "bg-emerald-600" : "bg-signal-blue/80")} style={{ width: `${(Math.abs(w.value) / max) * 100}%` }} />
                </div>
                <span className={cn("w-24 text-right font-mono tabnum", w.value < 0 ? "text-red-600" : "text-ink-primary")}>{usd(w.value)}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel className="p-4" testid="cost-breakdown">
          <div className="text-stitle text-ink-primary mb-3">Where the money goes</div>
          <div className="space-y-3">
            {costRows.map((c, i) => (
              <div key={i}>
                <div className="flex items-center justify-between text-compact mb-1">
                  <span className="text-ink-secondary">{c.label}</span>
                  <span className="font-mono tabnum text-red-600">{usd(c.value)}</span>
                </div>
                <div className="h-1.5 bg-surface-2 rounded-full overflow-hidden">
                  <div className="h-full bg-red-500/70 rounded-full" style={{ width: `${(Math.abs(c.value) / costMax) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
